import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Lightning,
  Info,
  Article,
  ArrowCircleRight,
  CheckCircle,
  Notepad,
  Sparkle,
  FloppyDisk,
  UploadSimple,
  FileText,
  X,
  Trash,
  Plus,
  CaretDown,
  CaretUp,
  Robot,
  DownloadSimple,
  Copy,
  Eye,
  Lightbulb,
  Quotes,
  StickerIcon,
  Users,
  Flame,
  Warning,
  ArrowsClockwise,
  MapTrifold,
  ChartBar,
} from '@phosphor-icons/react'
import api from '../services/api'
import { toast } from 'sonner'
import { useBilling, generateAction } from '../contexts/BillingContext'
import BRollPanel from './BRollPanel'
import CreditsMeter from './CreditsMeter'

const SECTION_META = {
  hook: { icon: Lightning, color: '#D42B1E', label: 'Hook' },
  intro: { icon: Info, color: '#007AFF', label: 'Intro' },
  desarrollo: { icon: Article, color: '#32ADE6', label: 'Desarrollo' },
  cta: { icon: ArrowCircleRight, color: '#FF9F0A', label: 'CTA' },
  outro: { icon: CheckCircle, color: '#5E5CE6', label: 'Outro' },
  custom: { icon: Notepad, color: '#A1A1A6', label: 'Custom' },
  // Mapa narrativo
  presentacion: { icon: Users, color: '#5E5CE6', label: 'Presentación' },
  detonante: { icon: Flame, color: '#FF6B35', label: 'Detonante' },
  tension: { icon: Warning, color: '#FF9F0A', label: 'Tensión' },
  resolucion: { icon: CheckCircle, color: '#30D158', label: 'Resolución' },
  cierre: { icon: ArrowsClockwise, color: '#BF5AF2', label: 'Cierre' },
}

const MUSIC_LAYER_LABELS = {
  silence: 'Silencio',
  pad_only: 'Solo pad',
  melody_enters: 'Melodía entra',
  full: 'Completa',
  stripped: 'Reducida',
  transition: 'Transición',
}

const TRANSITION_LABELS = {
  riser: 'Riser ↑',
  fade: 'Fade',
  cut: 'Corte',
  drop: 'Drop ↓',
  silence: 'Silencio',
}

const INTENSITY_OPTIONS = [
  { value: 'calm', label: 'Calma', color: '#32ADE6' },
  { value: 'tension', label: 'Tensión', color: '#FF9F0A' },
  { value: 'climax', label: 'Clímax', color: '#D42B1E' },
]

function wordCount(text) {
  return text ? text.trim().split(/\s+/).filter(Boolean).length : 0
}

function CopyButton({ text, size = 13 }) {
  const copy = () => {
    navigator.clipboard.writeText(text)
    toast.success('Copiado')
  }
  return (
    <button
      onClick={copy}
      className="p-1 text-[#6E6E73] hover:text-white transition-colors rounded"
      title="Copiar"
    >
      <Copy size={size} />
    </button>
  )
}

function AutoResizeTextarea({ value, onChange, placeholder, className, ...props }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto'
      ref.current.style.height = ref.current.scrollHeight + 'px'
    }
  }, [value])
  return (
    <textarea
      ref={ref}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      style={{ overflow: 'hidden' }}
      {...props}
    />
  )
}

/* ─── Concept panel ───────────────────────────────── */
function ConceptPanel({ concept }) {
  const [open, setOpen] = useState(false)
  if (!concept) return null
  return (
    <div
      className="bg-ds-elevated border border-[#BF5AF2]/20 overflow-hidden"
      style={{ borderRadius: 2 }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Lightbulb size={14} style={{ color: '#BF5AF2' }} weight="fill" />
          <span
            className="text-white text-sm font-semibold"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Concepto
          </span>
        </div>
        <div className="flex items-center gap-1">
          <CopyButton text={concept} />
          {open ? (
            <CaretUp size={14} className="text-[#6E6E73]" />
          ) : (
            <CaretDown size={14} className="text-[#6E6E73]" />
          )}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[#BF5AF2]/10 pt-3">
          <p className="text-[#D1D1D6] text-sm leading-relaxed">{concept}</p>
        </div>
      )}
    </div>
  )
}
/* ─── Key phrases panel ────────────────────────── */
function KeyPhrasesPanel({ phrases }) {
  const [open, setOpen] = useState(false)
  if (!phrases || phrases.length === 0) return null
  return (
    <div
      className="border border-[#FF9F0A]/20 overflow-hidden bg-ds-elevated"
      style={{ borderRadius: 2 }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Quotes size={14} className="text-[#FF9F0A]" weight="fill" />
          <span
            className="text-white text-sm font-semibold"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Frases clave
          </span>
          <span className="text-[10px] text-[#FF9F0A] bg-[#FF9F0A]/10 px-2 py-0.5 ">
            {phrases.length}
          </span>
        </div>
        {open ? (
          <CaretUp size={14} className="text-[#6E6E73]" />
        ) : (
          <CaretDown size={14} className="text-[#6E6E73]" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[#FF9F0A]/10 pt-3 space-y-2">
          {phrases.map((phrase, i) => (
            <div key={i} className="flex items-start gap-2 group">
              <span className="shrink-0 text-[#4A4A4A] text-xs mt-0.5">—</span>
              <p className="flex-1 text-[#D1D1D6] text-sm leading-relaxed italic">"{phrase}"</p>
              <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <CopyButton text={phrase} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
/* ─── Narrative panel ───────────────────────────────── */
function NarrativePanel({ narrative }) {
  const [open, setOpen] = useState(false)
  if (!narrative) return null
  return (
    <div
      className="border border-[#32ADE6]/25 overflow-hidden bg-ds-elevated"
      style={{ borderRadius: 2 }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Article size={14} className="text-[#32ADE6]" weight="duotone" />
          <span
            className="text-white text-sm font-semibold"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Narrativa completa
          </span>
          <span className="text-[10px] text-[#32ADE6] bg-[#32ADE6]/10 px-2 py-0.5 ">
            {wordCount(narrative)} palabras
          </span>
        </div>
        <div className="flex items-center gap-1">
          <CopyButton text={narrative} />
          {open ? (
            <CaretUp size={14} className="text-[#6E6E73]" />
          ) : (
            <CaretDown size={14} className="text-[#6E6E73]" />
          )}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-5 border-t border-[#32ADE6]/10 pt-4">
          <p className="text-[#C8D8E8] text-sm leading-[1.9] whitespace-pre-wrap">{narrative}</p>
        </div>
      )}
    </div>
  )
}

/* ─── Production notes panel ────────────────────────── */
function ProductionNotesPanel({ notes }) {
  const [open, setOpen] = useState(false)
  if (!notes) return null
  return (
    <div
      className="border border-[#FF9F0A]/20 overflow-hidden bg-ds-elevated"
      style={{ borderRadius: 2 }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <StickerIcon size={14} className="text-[#FF9F0A]" weight="duotone" />
          <span
            className="text-white text-sm font-medium"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Notas de producción
          </span>
        </div>
        <div className="flex items-center gap-1">
          <CopyButton text={notes} />
          {open ? (
            <CaretUp size={14} className="text-[#6E6E73]" />
          ) : (
            <CaretDown size={14} className="text-[#6E6E73]" />
          )}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[#FF9F0A]/10 pt-3">
          <p className="text-[#A1A1A6] text-sm leading-relaxed whitespace-pre-wrap">{notes}</p>
        </div>
      )}
    </div>
  )
}

/* ─── Visual brief panel ────────────────────────────── */
function VisualBriefPanel({ brief }) {
  const [open, setOpen] = useState(true)
  if (!brief) return null
  const p = brief.palette || {}
  const badges = [
    brief.texture,
    brief.typography_mood,
    brief.composition_style,
    brief.rhythm,
  ].filter(Boolean)
  return (
    <div
      className="border border-[#32ADE6]/25 overflow-hidden bg-ds-elevated"
      style={{ borderRadius: 2 }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px]">🎨</span>
          <span
            className="text-white text-sm font-semibold"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Brief visual
          </span>
          {p.primary && (
            <span
              className="w-3 h-3 shrink-0 inline-block"
              style={{ background: p.primary, borderRadius: 2 }}
            />
          )}
        </div>
        {open ? (
          <CaretUp size={14} className="text-[#6E6E73]" />
        ) : (
          <CaretDown size={14} className="text-[#6E6E73]" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-5 border-t border-[#32ADE6]/10 pt-4 space-y-4">
          {brief.metaphor && (
            <p className="text-[#D1D1D6] text-sm leading-relaxed italic">"{brief.metaphor}"</p>
          )}
          {brief.visual_identity && (
            <p className="text-[#A1A1A6] text-xs leading-relaxed">{brief.visual_identity}</p>
          )}
          {Object.keys(p).length > 0 && (
            <div className="space-y-2">
              <p className="text-[#6E6E73] text-[10px] uppercase tracking-widest">Paleta</p>
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[
                    { key: 'primary', label: 'Principal' },
                    { key: 'secondary', label: 'Secundario' },
                    { key: 'accent', label: 'Acento' },
                    { key: 'background', label: 'Fondo' },
                  ].map(({ key, label }) =>
                    p[key] ? (
                      <div key={key} className="flex items-center gap-1.5 group" title={label}>
                        <span
                          className="w-5 h-5 shrink-0 border border-white/10"
                          style={{ background: p[key], borderRadius: 2 }}
                        />
                        <span className="text-[10px] text-[#6E6E73] font-mono">{p[key]}</span>
                      </div>
                    ) : null
                  )}
                </div>
              </div>
              {p.intent && <p className="text-[#6E6E73] text-[11px] leading-relaxed">{p.intent}</p>}
            </div>
          )}
          {badges.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {badges.map(b => (
                <span
                  key={b}
                  className="text-[10px] px-2 py-0.5 bg-[#32ADE6]/10 text-[#32ADE6] border border-[#32ADE6]/20"
                  style={{ borderRadius: 2 }}
                >
                  {b.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
          {brief.reference_note && (
            <p className="text-[#6E6E73] text-[11px] leading-relaxed border-l-2 border-[#32ADE6]/30 pl-3">
              {brief.reference_note}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── Sound brief panel ─────────────────────────────── */
function SoundBriefPanel({ brief }) {
  const [open, setOpen] = useState(true)
  if (!brief) return null
  const arc = brief.arc || {}
  return (
    <div
      className="border border-[#BF5AF2]/20 overflow-hidden bg-ds-elevated"
      style={{ borderRadius: 2 }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px]">🎵</span>
          <span
            className="text-white text-sm font-semibold"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Brief sonoro
          </span>
        </div>
        {open ? (
          <CaretUp size={14} className="text-[#6E6E73]" />
        ) : (
          <CaretDown size={14} className="text-[#6E6E73]" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-5 border-t border-[#BF5AF2]/10 pt-4 space-y-4">
          {brief.musical_metaphor && (
            <p className="text-[#D1D1D6] text-sm leading-relaxed italic">
              "{brief.musical_metaphor}"
            </p>
          )}
          {brief.base_mood && (
            <p className="text-[#A1A1A6] text-xs leading-relaxed">{brief.base_mood}</p>
          )}
          {Object.keys(arc).length > 0 && (
            <div className="space-y-2">
              <p className="text-[#6E6E73] text-[10px] uppercase tracking-widest">Arco sonoro</p>
              <div className="space-y-2">
                {[
                  { key: 'inicio', label: 'Inicio' },
                  { key: 'desarrollo', label: 'Desarrollo' },
                  { key: 'climax', label: 'Clímax' },
                  { key: 'cierre', label: 'Cierre' },
                ].map(({ key, label }) =>
                  arc[key] ? (
                    <div key={key} className="flex items-start gap-2">
                      <span className="shrink-0 text-[9px] font-bold uppercase tracking-widest text-[#BF5AF2] mt-0.5 w-14">
                        {label}
                      </span>
                      <p className="text-[#A1A1A6] text-xs leading-relaxed flex-1">{arc[key]}</p>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          )}
          {brief.references && (
            <div>
              <p className="text-[#6E6E73] text-[10px] uppercase tracking-widest mb-1">
                Referencias
              </p>
              <p className="text-[#6E6E73] text-[11px] leading-relaxed border-l-2 border-[#BF5AF2]/30 pl-3">
                {brief.references}
              </p>
            </div>
          )}
          {brief.what_never && (
            <div>
              <p className="text-[#6E6E73] text-[10px] uppercase tracking-widest mb-1">
                Nunca hacer
              </p>
              <p className="text-[#FF453A] text-[11px] leading-relaxed opacity-80">
                {brief.what_never}
              </p>
            </div>
          )}
          {brief.voice_treatment && (
            <div>
              <p className="text-[#6E6E73] text-[10px] uppercase tracking-widest mb-1">Voz</p>
              <p className="text-[#A1A1A6] text-[11px] leading-relaxed">{brief.voice_treatment}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── Cinematography card ───────────────────────────── */
function CinematographyCard({ data }) {
  const [open, setOpen] = useState(false)
  if (!data || typeof data !== 'object') return null
  const rows = [
    { key: 'shot_type', label: 'Plano' },
    { key: 'camera_movement', label: 'Cámara' },
    { key: 'framing', label: 'Encuadre' },
    { key: 'staging', label: 'Staging' },
    { key: 'lighting', label: 'Luz' },
    { key: 'lens_feel', label: 'Óptica' },
    { key: 'rhythm_note', label: 'Ritmo' },
  ].filter(r => data[r.key])

  const summary = [data.shot_type, data.camera_movement].filter(Boolean).join(' · ')

  return (
    <div className="border-b border-ds-border">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div
          className="shrink-0 w-20 flex items-center justify-center py-1"
          style={{ borderRight: '1px solid #32ADE622', background: '#32ADE608' }}
        >
          <span className="text-[9px] font-bold uppercase tracking-widest text-[#32ADE6]">
            Cámara
          </span>
        </div>
        <span className="flex-1 text-[#A1A1A6] text-xs truncate pl-3">{summary || '—'}</span>
        {open ? (
          <CaretUp size={11} className="text-[#4A4A4A] shrink-0" />
        ) : (
          <CaretDown size={11} className="text-[#4A4A4A] shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-1.5 pl-[calc(5rem+13px)]">
          {rows.map(({ key, label }) => (
            <div key={key} className="flex gap-2 items-start">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#4A4A4A] w-14 shrink-0 pt-0.5">
                {label}
              </span>
              <span className="text-[#A1A1A6] text-xs leading-relaxed">{data[key]}</span>
            </div>
          ))}
          {data.eye_contact === false && (
            <div className="flex gap-2 items-start">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#4A4A4A] w-14 shrink-0 pt-0.5">
                Mirada
              </span>
              <span className="text-[#A1A1A6] text-xs">No mira a cámara</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── Sound design card ─────────────────────────────── */
function SoundDesignCard({ data }) {
  const [open, setOpen] = useState(false)
  if (!data || typeof data !== 'object') return null

  const layerLabel = MUSIC_LAYER_LABELS[data.music_layer] || data.music_layer || '—'
  const transLabel = TRANSITION_LABELS[data.transition_out] || data.transition_out || '—'

  return (
    <div className="border-b border-ds-border">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div
          className="shrink-0 w-20 flex items-center justify-center py-1"
          style={{ borderRight: '1px solid #5E5CE622', background: '#5E5CE608' }}
        >
          <span className="text-[9px] font-bold uppercase tracking-widest text-[#5E5CE6]">
            Sonido
          </span>
        </div>
        <span className="flex-1 text-[#A1A1A6] text-xs truncate pl-3">
          {layerLabel}
          {data.transition_out ? ` → ${transLabel}` : ''}
        </span>
        {open ? (
          <CaretUp size={11} className="text-[#4A4A4A] shrink-0" />
        ) : (
          <CaretDown size={11} className="text-[#4A4A4A] shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-1.5 pl-[calc(5rem+13px)]">
          {data.instruction && (
            <div className="flex gap-2 items-start">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#4A4A4A] w-14 shrink-0 pt-0.5">
                Acción
              </span>
              <span className="text-[#D1D1D6] text-xs leading-relaxed">{data.instruction}</span>
            </div>
          )}
          {data.mix_note && (
            <div className="flex gap-2 items-start">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#4A4A4A] w-14 shrink-0 pt-0.5">
                Mix
              </span>
              <span className="text-[#A1A1A6] text-xs leading-relaxed">{data.mix_note}</span>
            </div>
          )}
          {data.sfx && data.sfx.length > 0 && (
            <div className="flex gap-2 items-start">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#4A4A4A] w-14 shrink-0 pt-0.5">
                SFX
              </span>
              <div className="flex flex-wrap gap-1">
                {data.sfx.map((s, i) => (
                  <span
                    key={i}
                    className="text-[10px] bg-[#5E5CE6]/10 text-[#5E5CE6] px-1.5 py-0.5"
                    style={{ borderRadius: 2 }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 items-start">
            <span className="text-[9px] font-bold uppercase tracking-widest text-[#4A4A4A] w-14 shrink-0 pt-0.5">
              Salida
            </span>
            <span
              className="text-[10px] bg-[#5E5CE6]/10 text-[#5E5CE6] px-1.5 py-0.5"
              style={{ borderRadius: 2 }}
            >
              {transLabel}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Section card ──────────────────────────────────── */
function SectionCard({
  section,
  scriptId,
  onUpdateContent,
  onUpdateTitle,
  onUpdateMeta,
  onDelete,
  onGenerate,
  generating,
  onBRollUpdate,
  hasVisualBrief,
  onGenerateVisualBrief,
  briefLoading,
  sectionCost,
  hasByok,
  balance,
  onNeedCredits,
  proposal,
  onAcceptProposal,
  onDiscardProposal,
}) {
  const [expanded, setExpanded] = useState(true)
  const [showAI, setShowAI] = useState(false)
  const [instruction, setInstruction] = useState('')
  const [editingTitle, setEditingTitle] = useState(false)
  const [localTitle, setLocalTitle] = useState(section.title)
  const meta = SECTION_META[section.type] || SECTION_META.custom
  const Icon = meta.icon
  const wc = wordCount(section.content)
  const readSec = Math.ceil((wc / 130) * 60)
  const sectionAction = generateAction(sectionCost, { hasByok, total: balance })
  const retryAction = generateAction(sectionCost, {
    hasByok,
    total: balance,
    verb: 'Probar otra',
  })

  const handleTitleSave = () => {
    setEditingTitle(false)
    if (localTitle.trim() !== section.title) onUpdateTitle(section.id, localTitle.trim())
  }

  return (
    <div
      className="bg-ds-elevated border border-ds-border overflow-hidden transition-all duration-150 hover:border-ds-border-mid"
      style={{ borderRadius: 2 }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 md:gap-3 px-3 py-2.5 md:px-4 md:py-3">
        <div
          className="shrink-0 w-6 h-6 flex items-center justify-center"
          style={{ background: `${meta.color}18`, borderRadius: 2 }}
        >
          <Icon size={13} style={{ color: meta.color }} weight="fill" />
        </div>
        {editingTitle ? (
          <input
            value={localTitle}
            onChange={e => setLocalTitle(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={e => {
              if (e.key === 'Enter') handleTitleSave()
            }}
            className="flex-1 bg-transparent text-white text-sm font-medium focus:outline-none border-b border-white/20"
            autoFocus
          />
        ) : (
          <span
            className="flex-1 text-white text-sm font-medium cursor-text hover:text-[#A1A1A6] transition-colors"
            onClick={() => setEditingTitle(true)}
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {section.title}
          </span>
        )}
        {wc > 0 && (
          <span className="hidden sm:inline text-[#6E6E73] text-[10px] shrink-0">
            {wc} pal · ~{readSec}s
          </span>
        )}
        {/* Intensity dot — cycles calm → tension → climax → off */}
        {(() => {
          const order = [null, 'calm', 'tension', 'climax']
          const currentIdx = order.indexOf(section.intensity ?? null)
          const nextVal = order[(currentIdx + 1) % order.length]
          const current = INTENSITY_OPTIONS.find(o => o.value === section.intensity)
          return (
            <button
              onClick={e => {
                e.stopPropagation()
                onUpdateMeta(section.id, 'intensity', nextVal)
              }}
              className="shrink-0 p-1.5  transition-colors hover:bg-white/[0.05]"
              title={
                current
                  ? `Intensidad: ${current.label} (click para cambiar)`
                  : 'Sin intensidad (click para asignar)'
              }
            >
              <span
                className="text-base leading-none"
                style={{ color: current?.color || '#3A3A3A' }}
              >
                ●
              </span>
            </button>
          )
        })()}
        <button
          onClick={e => {
            e.stopPropagation()
            setShowAI(!showAI)
          }}
          className={`shrink-0 p-1.5  transition-colors ${showAI ? 'bg-[#007AFF]/15 text-[#007AFF]' : 'text-[#6E6E73] hover:text-[#007AFF]'}`}
          title="Generar con IA"
        >
          <Sparkle size={13} weight="fill" />
        </button>
        <button
          onClick={() => onDelete(section.id)}
          className="shrink-0 p-1.5  text-[#6E6E73] hover:text-ds-red transition-colors"
        >
          <Trash size={13} />
        </button>
        <button
          onClick={() => setExpanded(!expanded)}
          className="shrink-0 p-1.5 text-[#6E6E73] hover:text-white transition-colors"
        >
          {expanded ? <CaretUp size={13} /> : <CaretDown size={13} />}
        </button>
      </div>

      {/* AI panel */}
      {showAI && (
        <div className="px-4 pb-3 border-t border-ds-border bg-[#007AFF]/5">
          <div className="flex gap-2 mt-3">
            <input
              value={instruction}
              onChange={e => setInstruction(e.target.value)}
              placeholder="Instrucción adicional (ej: más energético, añade ejemplo)"
              className="ds-input flex-1 text-xs py-1.5"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  onGenerate(section.id, instruction)
                  setShowAI(false)
                  setInstruction('')
                }
              }}
            />
            <button
              onClick={() => {
                if (sectionAction.blocked) {
                  onNeedCredits?.()
                  return
                }
                onGenerate(section.id, instruction)
                setShowAI(false)
                setInstruction('')
              }}
              disabled={generating}
              className={`px-3 py-1.5 text-xs disabled:opacity-50 transition-colors font-medium ${
                sectionAction.blocked
                  ? 'bg-ds-elevated border border-ds-border-mid text-[#555555] hover:text-ds-text'
                  : 'bg-[#007AFF] text-white hover:bg-[#0066DD]'
              }`}
              style={{ borderRadius: 2 }}
              data-testid="section-generate"
            >
              {generating ? '...' : sectionAction.label}
            </button>
          </div>
        </div>
      )}

      {/* Propuesta de la IA — se compara, no se sustituye */}
      {proposal && (
        <div className="px-4 py-3 border-t border-ds-border bg-[#007AFF]/5">
          <div className="flex items-center justify-between mb-2.5">
            <span className="type-label text-ds-text">Propuesta de la IA</span>
            <span className="text-ds-muted text-xs" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Tu texto sigue intacto
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex flex-col">
              <p className="type-caption text-ds-muted mb-1.5">Lo que tienes</p>
              <div
                className="flex-1 bg-ds-bg border border-ds-border p-3 text-sm text-ds-muted whitespace-pre-wrap max-h-64 overflow-y-auto"
                style={{ borderRadius: 2, fontFamily: 'Barlow, sans-serif' }}
              >
                {section.content || 'Esta sección está vacía.'}
              </div>
            </div>
            <div className="flex flex-col">
              <p className="type-caption text-[#007AFF] mb-1.5">Lo que propone</p>
              <div
                className="flex-1 bg-ds-bg border border-[#007AFF]/40 p-3 text-sm text-ds-text whitespace-pre-wrap max-h-64 overflow-y-auto"
                style={{ borderRadius: 2, fontFamily: 'Barlow, sans-serif' }}
              >
                {proposal.content || 'La IA no devolvió texto.'}
              </div>
            </div>
          </div>
          {proposal.instruction && (
            <p className="text-[#4A4A4A] text-xs mt-2" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Instrucción: {proposal.instruction}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button
              onClick={() => onAcceptProposal(section.id)}
              disabled={generating || !proposal.content}
              className="px-3 py-1.5 bg-[#007AFF] text-white text-xs font-semibold hover:bg-[#0066DD] disabled:opacity-50 transition-colors"
              style={{ borderRadius: 2 }}
            >
              Usar esta
            </button>
            <button
              onClick={() => {
                if (retryAction.blocked) {
                  onNeedCredits?.()
                  return
                }
                onGenerate(section.id, proposal.instruction)
              }}
              disabled={generating}
              className="px-3 py-1.5 border border-ds-border-mid text-ds-muted hover:text-ds-text text-xs disabled:opacity-50 transition-colors"
              style={{ borderRadius: 2 }}
              data-testid="proposal-retry"
            >
              {generating ? 'Generando...' : retryAction.label}
            </button>
            <button
              onClick={() => onDiscardProposal(section.id)}
              disabled={generating}
              className="px-2 py-1.5 text-ds-muted hover:text-ds-text text-xs disabled:opacity-50 transition-colors"
            >
              Descartar
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-ds-border space-y-3">
          {generating ? (
            <div className="mt-3 flex items-center gap-3 py-6 justify-center">
              <div className="w-4 h-4 border-2 border-[#007AFF] border-t-transparent  animate-spin" />
              <span className="text-[#6E6E73] text-sm">Generando con IA...</span>
            </div>
          ) : (
            <>
              <AutoResizeTextarea
                value={section.content}
                onChange={e => onUpdateContent(section.id, e.target.value)}
                placeholder={`Escribe o genera con IA el "${section.title}"...`}
                className="ds-input resize-none leading-relaxed mt-3 min-h-[80px] w-full"
              />
              {/* Production fields — always visible */}
              <div className="border border-ds-border overflow-hidden" style={{ borderRadius: 2 }}>
                <CinematographyCard data={section.cinematography} />
                <SoundDesignCard data={section.sound_design} />
                {/* Emotion — editable */}
                <div className="flex items-stretch">
                  <div
                    className="shrink-0 w-20 flex items-center justify-center py-2"
                    style={{ borderRight: '1px solid #BF5AF222', background: '#BF5AF208' }}
                  >
                    <span className="text-[9px] font-bold uppercase tracking-widest text-[#BF5AF2]">
                      Emoción
                    </span>
                  </div>
                  <AutoResizeTextarea
                    value={section.emotion}
                    onChange={e => onUpdateMeta(section.id, 'emotion', e.target.value)}
                    placeholder="Qué debe sentir el espectador..."
                    className="flex-1 bg-transparent px-3 py-2 text-[#A1A1A6] text-xs placeholder-[#3A3A3A] focus:outline-none resize-none leading-relaxed min-h-[32px]"
                  />
                </div>
              </div>
              {/* B-Roll panel — recomendacion de escena visual Remotion */}
              {scriptId && onBRollUpdate && (
                <BRollPanel
                  section={section}
                  scriptId={scriptId}
                  onBRollUpdate={onBRollUpdate}
                  hasVisualBrief={hasVisualBrief}
                  onGenerateVisualBrief={onGenerateVisualBrief}
                  briefLoading={briefLoading}
                />
              )}
              {/* Quick iteration chips */}
              {section.content && (
                <div className="pt-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[#4A4A4A] text-[10px] mr-0.5">Refinar:</span>
                    {[
                      'Más corto',
                      'Más directo',
                      'Con tensión',
                      'Añade ejemplo',
                      'Más personal',
                    ].map(chip => (
                      <button
                        key={chip}
                        onClick={() => {
                          setInstruction(chip)
                          onGenerate(section.id, chip)
                        }}
                        disabled={generating}
                        className="px-2 py-0.5 border border-ds-border text-ds-muted hover:text-ds-text hover:border-[#007AFF]/40 text-[10px] transition-all disabled:opacity-40"
                        style={{ borderRadius: 2 }}
                      >
                        {chip}
                      </button>
                    ))}
                    {section.last_instruction && (
                      <span className="text-[#4A4A4A] text-[10px] ml-1 italic">
                        última: &ldquo;{section.last_instruction}&rdquo;
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── Emotional arc strip ─────────────────────── */
function EmotionalArcStrip({ sections }) {
  const withIntensity = sections.filter(s => s.intensity)
  if (withIntensity.length === 0) return null
  return (
    <div className="flex items-center gap-2 px-1">
      <span className="text-[#3A3A3A] text-[10px] shrink-0">Arco:</span>
      <div className="flex items-center gap-1">
        {sections.map(s => {
          const opt = INTENSITY_OPTIONS.find(o => o.value === s.intensity)
          return (
            <div
              key={s.id}
              title={`${s.title}${opt ? ` — ${opt.label}` : ''}`}
              className="w-2.5 h-2.5  transition-all"
              style={{ background: opt?.color || '#2A2A2A', opacity: opt ? 1 : 0.3 }}
            />
          )
        })}
      </div>
    </div>
  )
}
/* ─── Context panel ─────────────────────────────────── */
function ContextPanel({ script, scriptId, onScriptUpdated }) {
  const [open, setOpen] = useState(false)
  const [contextText, setContextText] = useState(script?.context_text || '')
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef(null)

  useEffect(() => {
    setContextText(script?.context_text || '')
  }, [script?.context_text])

  const saveContextText = async () => {
    try {
      const res = await api.put(`/scripts/${scriptId}`, {
        context_text: contextText,
      })
      onScriptUpdated(res.data)
      toast.success('Contexto guardado')
    } catch {
      toast.error('Error al guardar contexto')
    }
  }

  const uploadFile = async e => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post(`/scripts/${scriptId}/upload-context`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onScriptUpdated({
        ...script,
        context_files: [...(script.context_files || []), res.data.file],
      })
      toast.success(`"${file.name}" añadido como contexto`)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir archivo')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const deleteFile = async fileId => {
    try {
      await api.delete(`/scripts/${scriptId}/context-file/${fileId}`)
      onScriptUpdated({
        ...script,
        context_files: script.context_files.filter(f => f.id !== fileId),
      })
      toast.success('Archivo eliminado')
    } catch {
      toast.error('Error al eliminar')
    }
  }

  const files = script?.context_files || []

  return (
    <div
      className="border border-ds-border overflow-hidden bg-ds-elevated"
      style={{ borderRadius: 2 }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Robot size={14} className="text-[#007AFF]" weight="duotone" />
          <span
            className="text-white text-sm font-medium"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Contexto para la IA
          </span>
          {(contextText || files.length > 0) && (
            <span
              className="text-[10px] bg-[#007AFF]/15 text-[#007AFF] px-2 py-0.5"
              style={{ borderRadius: 2 }}
            >
              {files.length > 0 ? `${files.length} archivo${files.length > 1 ? 's' : ''}` : ''}
              {contextText && files.length > 0 ? ' + texto' : contextText ? 'con texto' : ''}
            </span>
          )}
        </div>
        {open ? (
          <CaretUp size={14} className="text-[#6E6E73]" />
        ) : (
          <CaretDown size={14} className="text-[#6E6E73]" />
        )}
      </button>
      {open && (
        <div className="border-t border-ds-border px-4 pb-4 space-y-4">
          <div className="mt-3">
            <label className="text-[#6E6E73] text-xs uppercase tracking-widest mb-1.5 block">
              Instrucciones / Briefing
            </label>
            <textarea
              value={contextText}
              onChange={e => setContextText(e.target.value)}
              placeholder="Ej: Escribe en tono informal. El video es para principiantes. Estilo: rápido con cortes..."
              rows={4}
              className="ds-input resize-none w-full"
            />
            <button
              onClick={saveContextText}
              className="mt-2 px-3 py-1.5 bg-ds-elevated border border-ds-border  text-[#A1A1A6] hover:text-white text-xs transition-colors"
            >
              Guardar contexto
            </button>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[#6E6E73] text-xs uppercase tracking-widest">
                Documentos de Referencia
              </label>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-ds-elevated border border-ds-border  text-xs text-[#A1A1A6] hover:text-white disabled:opacity-50 transition-colors"
              >
                {uploading ? (
                  <div className="w-3 h-3 border border-current border-t-transparent  animate-spin" />
                ) : (
                  <UploadSimple size={12} />
                )}
                PDF / TXT / MD
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.txt,.md"
                onChange={uploadFile}
                className="hidden"
              />
            </div>
            {files.length > 0 ? (
              <div className="space-y-2">
                {files.map(f => (
                  <div
                    key={f.id}
                    className="flex items-center gap-2 p-2 bg-ds-elevated border border-ds-border"
                    style={{ borderRadius: 2 }}
                  >
                    <FileText size={14} className="text-[#007AFF] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-medium truncate">{f.name}</p>
                      <p className="text-[#6E6E73] text-[10px]">
                        {f.char_count?.toLocaleString() || 0} caracteres extraídos
                      </p>
                    </div>
                    <button
                      onClick={() => deleteFile(f.id)}
                      className="text-[#6E6E73] hover:text-ds-red transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#4A4A4A] text-xs text-center py-3">
                Sube PDFs, guías de estilo o briefings para que la IA los use como referencia
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Channel Insights Panel ────────────────────────── */
function ChannelInsightsPanel({ insights }) {
  const [open, setOpen] = useState(false)
  if (!insights || insights.video_count === 0) return null
  const top = insights.top_retention_videos || []
  const mainSource = insights.top_traffic_sources?.[0]
  return (
    <div
      className="border border-[#BF5AF2]/20 bg-[#BF5AF2]/5 overflow-hidden"
      style={{ borderRadius: 2 }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
      >
        <div className="flex items-center gap-2">
          <ChartBar size={13} style={{ color: '#BF5AF2' }} weight="duotone" />
          <span className="text-[#BF5AF2] text-xs font-semibold">Insights del canal</span>
          <span className="text-[#6E6E73] text-[10px]">
            · aplicados a la generación · {insights.qualifying_count || insights.video_count} vídeos
          </span>
        </div>
        {open ? (
          <CaretUp size={12} className="text-[#6E6E73]" />
        ) : (
          <CaretDown size={12} className="text-[#6E6E73]" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2 border-t border-[#BF5AF2]/10 pt-3">
          {top.length > 0 && (
            <div>
              <p className="text-[#6E6E73] text-[10px] uppercase tracking-widest mb-1.5">
                Mayor retención ({insights.platform || 'YouTube'})
              </p>
              {top.map((v, i) => (
                <div key={i} className="flex items-center gap-2 py-0.5">
                  <span className="text-[#6E6E73] text-[10px] w-3 shrink-0">{i + 1}.</span>
                  <span className="text-[#A1A1A6] text-xs truncate flex-1">{v.title}</span>
                  <span className="text-[#BF5AF2] text-xs shrink-0 font-medium">
                    {v.avg_view_percentage}%
                  </span>
                  {v.estimated_duration_min && (
                    <span className="text-[#6E6E73] text-xs shrink-0">
                      ~{v.estimated_duration_min} min
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-4 flex-wrap">
            {insights.optimal_duration_min && (
              <p className="text-xs">
                <span className="text-[#6E6E73]">Duración óptima: </span>
                <span className="text-white font-medium">~{insights.optimal_duration_min} min</span>
              </p>
            )}
            {mainSource && (
              <p className="text-xs">
                <span className="text-[#6E6E73]">Fuente principal: </span>
                <span className="text-white font-medium">
                  {mainSource.label} ({mainSource.pct}%)
                </span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Generation progress phases shown to the user
const GENERATION_PHASES = [
  'Leyendo tu perfil y contexto del vídeo...',
  'Definiendo la verdad incómoda y el arco emocional...',
  'Construyendo la narrativa completa...',
  'Dividiendo en secciones con cinematografía por sección...',
  'Generando brief visual — paleta, metáfora, textura...',
  'Componiendo el diseño sonoro en capas...',
  'Finalizando y guardando el guión...',
]

/* ─── Generation progress overlay ───────────────────── */
function GenerationProgress({ phase }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div
        className="bg-ds-surface border border-ds-border px-8 py-7 max-w-sm w-full mx-4"
        style={{ borderRadius: 2 }}
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-5 h-5 border-2 border-[#007AFF] border-t-transparent  animate-spin shrink-0" />
          <span
            className="text-white font-semibold text-sm"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Generando guión
          </span>
        </div>
        <p className="text-[#A1A1A6] text-sm leading-relaxed min-h-[40px] transition-all duration-500">
          {phase}
        </p>
        <p className="text-[#4A4A4A] text-xs mt-4">
          No cierres esta pestaña — el guión se está construyendo
        </p>
      </div>
    </div>
  )
}

/* ─── Recovery banner ────────────────────────────────── */
function RecoveryBanner({ onRefresh }) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 bg-[#FF9F0A]/10 border border-[#FF9F0A]/30"
      style={{ borderRadius: 2 }}
    >
      <div className="w-3 h-3 border-2 border-[#FF9F0A] border-t-transparent  animate-spin shrink-0" />
      <p className="flex-1 text-[#FF9F0A] text-sm">
        Tu guión se está generando en segundo plano...
      </p>
      <button
        onClick={onRefresh}
        className="text-[#FF9F0A] text-xs underline shrink-0 hover:no-underline"
      >
        Comprobar ahora
      </button>
    </div>
  )
}

const POLL_INTERVAL_MS = 3000
// Margen amplio sobre el plazo con el que el backend da por perdida una
// generación (GENERATION_STALE_AFTER_SECONDS, 10 min por defecto).
const POLL_GIVE_UP_MS = 15 * 60 * 1000

/* ─── Failed generation banner ───────────────────────── */
function GenerationFailedBanner({ onRetry, onDismiss }) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 bg-ds-red/10 border border-ds-red/30"
      style={{ borderRadius: 2 }}
    >
      <p className="flex-1 text-ds-red text-sm">
        La generación anterior se interrumpió. Si te cobró créditos, ya se te han devuelto.
      </p>
      <button
        onClick={onRetry}
        className="text-ds-red text-xs underline shrink-0 hover:no-underline"
      >
        Reintentar
      </button>
      <button
        onClick={onDismiss}
        className="text-[#A1A1A6] text-xs shrink-0 hover:text-white"
        aria-label="Descartar aviso"
      >
        ✕
      </button>
    </div>
  )
}

/* ─── Main editor ───────────────────────────────────── */
export default function ScriptEditor({ scriptId, onSaved, onDirtyChange }) {
  const [script, setScript] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { hasByok, credits, refresh: refreshBilling } = useBilling()
  const fullCost = script?.credit_cost?.full
  const balance = credits?.total ?? 0
  const fullAction = generateAction(fullCost, { hasByok, total: balance })
  // El panel de saldo se abre desde el contador o desde un botón que no puede
  // generar: quedarse sin créditos tiene que llevar a algún sitio.
  const [creditsOpen, setCreditsOpen] = useState(false)
  const bodyRef = useRef(null)
  // El panel vive en la barra de arriba: abrirlo desde una sección de más abajo
  // sería abrirlo fuera de la pantalla.
  const openCreditsPanel = useCallback(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    setCreditsOpen(true)
  }, [])
  const [generatingAll, setGeneratingAll] = useState(false)
  const [generatingSection, setGeneratingSection] = useState(null)
  // Propuestas pendientes de decisión, por id de sección. Viven solo aquí:
  // el backend no ha escrito nada todavía.
  const [proposals, setProposals] = useState({})
  const [suggestingBRoll, setSuggestingBRoll] = useState(false)
  const [briefLoading, setBriefLoading] = useState(false)
  const [versionName, setVersionName] = useState('')
  const [editingVersionName, setEditingVersionName] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [channelInsights, setChannelInsights] = useState(null)
  const [generationPhase, setGenerationPhase] = useState(0)
  const phaseTimerRef = useRef(null)
  const pollTimerRef = useRef(null)

  // output_mode: 'escaleta' | 'guion' — persiste en el script
  const outputMode = script?.output_mode || 'guion'
  const setOutputMode = async nextMode => {
    if (nextMode === outputMode) return
    setScript(s => (s ? { ...s, output_mode: nextMode } : s))
    try {
      await api.put(`/scripts/${scriptId}`, { output_mode: nextMode })
    } catch {
      toast.error('No se pudo guardar el modo de salida')
      setScript(s => (s ? { ...s, output_mode: outputMode } : s))
    }
  }

  const markDirty = useCallback(val => setIsDirty(val), [])

  // Una propuesta sin decidir también es trabajo que se pierde al cambiar de
  // guion, así que cuenta como cambio pendiente de cara al aviso de salida.
  const hasProposals = Object.keys(proposals).length > 0
  useEffect(() => {
    onDirtyChange?.(isDirty || hasProposals)
  }, [isDirty, hasProposals, onDirtyChange])

  // Advance generation phase message every ~6 seconds
  const startPhaseTimer = useCallback(() => {
    setGenerationPhase(0)
    phaseTimerRef.current = setInterval(() => {
      setGenerationPhase(p => Math.min(p + 1, GENERATION_PHASES.length - 1))
    }, 6000)
  }, [])

  const stopPhaseTimer = useCallback(() => {
    if (phaseTimerRef.current) {
      clearInterval(phaseTimerRef.current)
      phaseTimerRef.current = null
    }
  }, [])

  // Poll script status when user returns after navigating away mid-generation.
  // Tiene final: el backend cierra por plazo las generaciones que mueren sin
  // avisar, y si ni siquiera eso llega, aquí se deja de preguntar en vez de
  // sondear para siempre contra un estado que ya no va a cambiar.
  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return
    const deadline = Date.now() + POLL_GIVE_UP_MS
    const stop = () => {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
    pollTimerRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        stop()
        setScript(s => (s ? { ...s, generation_status: 'failed' } : s))
        return
      }
      try {
        const res = await api.get(`/scripts/${scriptId}`)
        const status = res.data.generation_status
        if (status === 'completed' || status === 'failed' || !status) {
          stop()
          setScript(res.data)
          setVersionName(res.data.version_name)
          if (status === 'completed') toast.success('¡Guión generado!')
          if (status === 'failed') toast.error('La generación falló. Inténtalo de nuevo.')
        }
      } catch {
        stop()
      }
    }, POLL_INTERVAL_MS)
  }, [scriptId])

  useEffect(() => {
    return () => {
      stopPhaseTimer()
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [stopPhaseTimer])

  const fetchScript = useCallback(async () => {
    if (!scriptId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get(`/scripts/${scriptId}`)
      setScript(res.data)
      setVersionName(res.data.version_name)
      markDirty(false)
      // If script was generating when user navigated away, resume polling
      if (res.data.generation_status === 'generating') {
        startPolling()
      }
      // Cargar insights del canal para la plataforma de este proyecto
      if (res.data.project_id) {
        api
          .get(`/projects/${res.data.project_id}`)
          .then(proj => {
            const platform = proj.data.platform || 'YouTube'
            return api.get(`/ai/channel-insights?platform=${encodeURIComponent(platform)}`)
          })
          .then(r => setChannelInsights(r.data))
          .catch(() => setChannelInsights({}))
      }
    } catch {
      toast.error('Error al cargar guión')
    } finally {
      setLoading(false)
    }
  }, [scriptId, markDirty, startPolling])

  useEffect(() => {
    fetchScript()
  }, [fetchScript])

  // beforeunload guard — generación en curso o propuestas sin decidir
  useEffect(() => {
    const handler = e => {
      if (generatingAll || hasProposals) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [generatingAll, hasProposals])

  // Ctrl+S / Cmd+S to save
  useEffect(() => {
    const handler = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (isDirty && !saving) saveScript()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, saving, script, versionName, editingVersionName])

  const updateSectionContent = (id, content) => {
    setScript(s => ({
      ...s,
      sections: s.sections.map(sec => (sec.id === id ? { ...sec, content } : sec)),
    }))
    markDirty(true)
  }
  const updateSectionTitle = (id, title) => {
    setScript(s => ({
      ...s,
      sections: s.sections.map(sec => (sec.id === id ? { ...sec, title } : sec)),
    }))
    markDirty(true)
  }
  const updateSectionMeta = (id, key, value) => {
    setScript(s => ({
      ...s,
      sections: s.sections.map(sec => (sec.id === id ? { ...sec, [key]: value } : sec)),
    }))
    markDirty(true)
  }
  const deleteSection = id => {
    setScript(s => ({
      ...s,
      sections: s.sections.filter(sec => sec.id !== id),
    }))
    markDirty(true)
  }
  const addSection = () => {
    setScript(s => ({
      ...s,
      sections: [
        ...(s.sections || []),
        {
          id: `custom-${Date.now()}`,
          type: 'custom',
          title: 'Nueva Sección',
          content: '',
          order: s.sections?.length || 0,
        },
      ],
    }))
    markDirty(true)
  }

  // Pide una propuesta, no una sustitución: el backend no toca el texto
  // guardado y aquí se guarda aparte hasta que el usuario decide.
  const generateSection = async (sectionId, instruction) => {
    setGeneratingSection(sectionId)
    try {
      const res = await api.post(`/scripts/${scriptId}/generate`, {
        section_id: sectionId,
        extra_instruction: instruction,
        preview: true,
      })
      setProposals(p => ({
        ...p,
        [sectionId]: { content: res.data.content ?? '', instruction: instruction || '' },
      }))
      refreshBilling()
      toast.success('Propuesta lista — compárala antes de aplicarla')
    } catch {
      toast.error('Error al generar sección')
    } finally {
      setGeneratingSection(null)
    }
  }

  const discardProposal = sectionId =>
    setProposals(p => {
      const { [sectionId]: _discarded, ...rest } = p
      return rest
    })

  // Aceptar sí escribe, y escribe de una vez: el usuario ya ha confirmado.
  const acceptProposal = async sectionId => {
    const proposal = proposals[sectionId]
    if (!proposal) return
    const nextSections = script.sections.map(sec =>
      sec.id === sectionId
        ? { ...sec, content: proposal.content, last_instruction: proposal.instruction || null }
        : sec
    )
    setScript(s => ({ ...s, sections: nextSections }))
    discardProposal(sectionId)
    setSaving(true)
    try {
      const res = await api.put(`/scripts/${scriptId}`, {
        sections: nextSections,
        version_name: script.version_name,
      })
      setScript(s => ({ ...res.data, credit_cost: s?.credit_cost }))
      markDirty(false)
      onSaved?.(res.data)
      toast.success('Sección actualizada')
    } catch {
      markDirty(true)
      toast.error('No se pudo guardar — el texto está en el editor, dale a Guardar')
    } finally {
      setSaving(false)
    }
  }

  const generateAll = async (overrides = {}) => {
    setGeneratingAll(true)
    startPhaseTimer()
    try {
      const res = await api.post(`/scripts/${scriptId}/generate`, {
        section_id: null,
        extra_instruction: overrides.extra_instruction || '',
        ...overrides,
      })
      setScript(s => ({
        ...s,
        sections: res.data.sections,
        concept: res.data.concept || s.concept,
        narrative: res.data.narrative || s.narrative,
        key_phrases: res.data.key_phrases || s.key_phrases,
        production_notes: res.data.production_notes || s.production_notes,
        last_raw_response: res.data.raw_response || s.last_raw_response,
        visual_brief: res.data.visual_brief || s.visual_brief,
        sound_brief: res.data.sound_brief || s.sound_brief,
        generation_status: 'completed',
      }))
      const hasContent = res.data.sections?.some(s => s.content)
      if (!hasContent)
        toast.warning('Claude no devolvió secciones estructuradas — revisa la respuesta completa')
      else toast.success('Guión generado')
      refreshBilling()
      markDirty(false)
    } catch (err) {
      const raw = err.response?.data?.detail
      const msg =
        typeof raw === 'object'
          ? raw?.message || 'Error al generar guión'
          : raw || 'Error al generar guión'
      if (err.response?.status === 402) {
        // UpgradePrompt already shown via api.js interceptor — suppress duplicate toast
      } else if (err.response?.status === 409) {
        // Ya hay otra generación viva para este guion (doble envío, otra
        // pestaña). En vez de un error seco, el editor se engancha a ella.
        toast.error(msg)
        fetchScript()
      } else if (err.response?.status === 400 && msg.includes('perfil')) {
        toast.error(msg, { duration: 6000 })
        setTimeout(() => (window.location.href = '/profile'), 2000)
      } else {
        toast.error(msg)
      }
    } finally {
      stopPhaseTimer()
      setGeneratingAll(false)
    }
  }

  const suggestAllBRoll = async () => {
    setSuggestingBRoll(true)
    try {
      const res = await api.post(`/scripts/${scriptId}/broll/suggest`, { section_ids: null })
      const recs = res.data.recommendations || {}
      setScript(s => ({
        ...s,
        sections: s.sections.map(sec => (recs[sec.id] ? { ...sec, broll: recs[sec.id] } : sec)),
      }))
      const count = Object.keys(recs).length
      if (count > 0)
        toast.success(`B-Roll dirigido para ${count} sección${count !== 1 ? 'es' : ''}`)
    } catch (err) {
      if (err.response?.status !== 402) {
        toast.error('Error al sugerir B-Roll')
      }
    } finally {
      setSuggestingBRoll(false)
    }
  }

  const generateVisualBrief = async () => {
    setBriefLoading(true)
    try {
      const res = await api.post(`/scripts/${scriptId}/broll/visual-brief`)
      setScript(s => ({ ...s, visual_brief: res.data.visual_brief }))
      toast.success('Brief visual generado — coherencia estética activada')
    } catch (err) {
      if (err.response?.status !== 402) {
        toast.error('Error al generar el brief visual')
      }
    } finally {
      setBriefLoading(false)
    }
  }

  const saveScript = async () => {
    setSaving(true)
    try {
      const res = await api.put(`/scripts/${scriptId}`, {
        sections: script.sections,
        version_name: editingVersionName ? versionName : script.version_name,
      })
      // El coste solo lo calcula el GET del guion: conservarlo al guardar.
      setScript(s => ({ ...res.data, credit_cost: s?.credit_cost }))
      markDirty(false)
      onSaved?.(res.data)
      toast.success('Guión guardado')
    } catch {
      toast.error('Error al guardar')
    } finally {
      setSaving(false)
      setEditingVersionName(false)
    }
  }

  const exportScript = () => {
    if (!script) return
    const parts = [`GUIÓN: ${script.version_name}`, '='.repeat(50), '']
    if (script.concept) parts.push('CONCEPTO', script.concept, '')
    if (script.narrative) parts.push('NARRATIVA COMPLETA', script.narrative, '')
    parts.push('SECCIONES')
    script.sections.forEach(s => {
      parts.push(`\n[${s.title.toUpperCase()}]`, s.content || '(sin contenido)')
      if (s.cinematography) {
        const c = s.cinematography
        const lines = [
          c.shot_type && `Plano: ${c.shot_type}`,
          c.camera_movement && `Movimiento: ${c.camera_movement}`,
          c.framing && `Encuadre: ${c.framing}`,
          c.staging && `Puesta en escena: ${c.staging}`,
          c.lighting && `Luz: ${c.lighting}`,
          c.lens_feel && `Óptica: ${c.lens_feel}`,
          c.rhythm_note && `Ritmo: ${c.rhythm_note}`,
          c.eye_contact !== undefined && `Eye contact: ${c.eye_contact ? 'sí' : 'no'}`,
        ].filter(Boolean)
        if (lines.length) parts.push('CINEMATOGRAFÍA:', ...lines.map(l => `  ${l}`))
      }
      if (s.sound_design) {
        const sd = s.sound_design
        const lines = [
          sd.music_layer && `Capa musical: ${sd.music_layer}`,
          sd.instruction && `Instrucción: ${sd.instruction}`,
          sd.mix_note && `Mix: ${sd.mix_note}`,
          sd.sfx?.length && `SFX: ${sd.sfx.join(', ')}`,
          sd.transition_out && `Transición: ${sd.transition_out}`,
        ].filter(Boolean)
        if (lines.length) parts.push('DISEÑO SONORO:', ...lines.map(l => `  ${l}`))
      }
      if (s.emotion) parts.push(`EMOCIÓN: ${s.emotion}`)
    })
    if (script.key_phrases?.length) {
      parts.push('', 'FRASES CLAVE')
      script.key_phrases.forEach(p => parts.push(`— "${p}"`))
    }
    if (script.production_notes) parts.push('', 'NOTAS DE PRODUCCIÓN', script.production_notes)
    parts.push('', '---', 'Generado con RDK Content Creator')
    const blob = new Blob([parts.join('\n')], {
      type: 'text/plain;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${script.version_name.replace(/\s+/g, '_')}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const copyAll = () => {
    if (!script) return
    navigator.clipboard.writeText(
      script.sections.map(s => `${s.title}:\n${s.content}`).join('\n\n')
    )
    toast.success('Copiado al portapapeles')
  }

  if (loading)
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-ds-red border-t-transparent  animate-spin" />
      </div>
    )
  if (!script) return null

  const isRecovering = script?.generation_status === 'generating' && !generatingAll
  const generationFailed = script?.generation_status === 'failed' && !generatingAll

  return (
    <div className="flex flex-col h-full">
      {/* Generation overlay — shown when actively generating */}
      {generatingAll && <GenerationProgress phase={GENERATION_PHASES[generationPhase]} />}
      {/* Top bar */}
      <div className="flex items-center gap-2 md:gap-3 px-4 md:px-6 py-3 border-b border-ds-border shrink-0">
        {editingVersionName ? (
          <input
            value={versionName}
            onChange={e => setVersionName(e.target.value)}
            onBlur={() => setEditingVersionName(false)}
            onKeyDown={e => {
              if (e.key === 'Enter') setEditingVersionName(false)
            }}
            className="flex-1 bg-transparent text-white font-semibold text-sm focus:outline-none border-b border-white/20"
            autoFocus
          />
        ) : (
          <h2
            className="flex-1 text-white font-semibold text-sm cursor-text hover:text-[#A1A1A6] transition-colors truncate"
            onClick={() => setEditingVersionName(true)}
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {script.version_name}
            {isDirty && (
              <span className="text-[#FF9F0A] text-[10px] ml-2 font-normal">● sin guardar</span>
            )}
          </h2>
        )}
        <div className="flex items-center gap-1.5">
          <button
            onClick={copyAll}
            className="p-2 text-[#6E6E73] hover:text-white transition-colors hover:bg-white/5"
            title="Copiar todo"
          >
            <Copy size={14} />
          </button>
          <button
            onClick={exportScript}
            className="p-2 text-[#6E6E73] hover:text-white transition-colors hover:bg-white/5"
            title="Exportar TXT"
          >
            <DownloadSimple size={14} />
          </button>
          <div className="w-px h-4 bg-ds-border mx-1" />
          <CreditsMeter open={creditsOpen} onOpenChange={setCreditsOpen} />
          <div className="w-px h-4 bg-ds-border mx-1" />
          {/* Escaleta / Guion toggle (F2 — wedge cultural ES) */}
          <div
            className="inline-flex items-center border border-ds-border-mid text-[10px] tracking-[0.08em] uppercase font-display font-bold"
            style={{ borderRadius: 2 }}
            role="group"
            aria-label="Modo de salida"
          >
            <button
              type="button"
              onClick={() => setOutputMode('escaleta')}
              className={`px-2.5 py-1.5 transition-colors ${
                outputMode === 'escaleta'
                  ? 'bg-ds-text text-ds-bg'
                  : 'text-ds-muted hover:text-white'
              }`}
              title="Escaleta — bullets jerárquicos con frases-ancla"
            >
              Escaleta
            </button>
            <button
              type="button"
              onClick={() => setOutputMode('guion')}
              className={`px-2.5 py-1.5 transition-colors ${
                outputMode === 'guion' ? 'bg-ds-text text-ds-bg' : 'text-ds-muted hover:text-white'
              }`}
              title="Guion — prosa completa para leer en voz alta"
            >
              Guion
            </button>
          </div>
          <button
            onClick={() => (fullAction.blocked ? setCreditsOpen(true) : generateAll())}
            disabled={generatingAll}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold disabled:opacity-50 transition-colors ${
              fullAction.blocked
                ? 'bg-ds-elevated border border-ds-border-mid text-[#555555] hover:text-ds-text'
                : 'bg-[#007AFF] text-white hover:bg-[#0066DD]'
            }`}
            style={{ borderRadius: 2 }}
            data-testid="editor-generate-all"
          >
            {generatingAll ? (
              <div className="w-3 h-3 border border-white border-t-transparent animate-spin" />
            ) : (
              !fullAction.blocked && <Sparkle size={12} weight="fill" />
            )}
            {generatingAll ? 'Generando...' : fullAction.label}
          </button>
          <button
            onClick={saveScript}
            disabled={saving}
            className="btn-accent flex items-center gap-1.5 px-3 py-1.5 text-xs disabled:opacity-50"
          >
            {saving ? (
              <div className="w-3 h-3 border border-white border-t-transparent animate-spin" />
            ) : (
              <FloppyDisk size={12} weight="fill" />
            )}
            Guardar
          </button>
        </div>
      </div>

      {/* Body */}
      <div ref={bodyRef} className="flex-1 overflow-y-auto px-3 md:px-6 py-5 space-y-3">
        {/* Recovery banner */}
        {isRecovering && <RecoveryBanner onRefresh={fetchScript} />}
        {generationFailed && (
          <GenerationFailedBanner
            onRetry={() => generateAll()}
            onDismiss={() => setScript(s => (s ? { ...s, generation_status: null } : s))}
          />
        )}

        {/* Contexto para la IA — accesible antes de generar */}
        <ContextPanel script={script} scriptId={scriptId} onScriptUpdated={setScript} />

        {/* Secciones */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between min-h-[20px]">
            <EmotionalArcStrip sections={script.sections || []} />
            <button
              onClick={async () => {
                if (script.sections?.some(s => s.content?.trim())) {
                  if (
                    !window.confirm(
                      '¿Reemplazar las secciones con el mapa narrativo? Se perderá el contenido.'
                    )
                  )
                    return
                }
                try {
                  const res = await api.post(`/scripts/${scriptId}/apply-template`)
                  setScript(res.data)
                  toast.success('Mapa narrativo aplicado')
                } catch {
                  toast.error('Error al aplicar la plantilla')
                }
              }}
              className="flex items-center gap-1.5 text-[10px] text-ds-muted hover:text-[#BF5AF2] border border-ds-border hover:border-[#BF5AF2]/30 px-2.5 py-1.5 transition-all shrink-0"
              style={{ borderRadius: 2 }}
            >
              <MapTrifold size={11} />
              Mapa narrativo
            </button>
          </div>
          {script.sections?.map(section => (
            <SectionCard
              key={section.id}
              section={section}
              scriptId={scriptId}
              onUpdateContent={updateSectionContent}
              onUpdateTitle={updateSectionTitle}
              onUpdateMeta={updateSectionMeta}
              onDelete={deleteSection}
              onGenerate={generateSection}
              generating={generatingSection === section.id}
              onBRollUpdate={updateSectionMeta}
              hasVisualBrief={!!script?.visual_brief}
              onGenerateVisualBrief={generateVisualBrief}
              briefLoading={briefLoading}
              sectionCost={script?.credit_cost?.section}
              hasByok={hasByok}
              balance={balance}
              onNeedCredits={openCreditsPanel}
              proposal={proposals[section.id]}
              onAcceptProposal={acceptProposal}
              onDiscardProposal={discardProposal}
            />
          ))}
        </div>

        {/* Añadir sección */}
        <button
          onClick={addSection}
          className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-ds-border text-ds-muted hover:text-ds-text hover:border-ds-border-mid transition-all text-xs"
          style={{ borderRadius: 2 }}
        >
          <Plus size={13} />
          Añadir sección
        </button>

        {/* Briefs de producción — el output premium */}
        {(script.visual_brief || script.sound_brief) && (
          <div className="space-y-2.5 pt-1">
            <VisualBriefPanel brief={script.visual_brief} />
            <SoundBriefPanel brief={script.sound_brief} />
          </div>
        )}

        {/* Outputs IA secundarios */}
        {(script.concept ||
          script.narrative ||
          script.key_phrases?.length > 0 ||
          script.production_notes) && (
          <div className="space-y-2">
            <ConceptPanel concept={script.concept} />
            <NarrativePanel narrative={script.narrative} />
            <KeyPhrasesPanel phrases={script.key_phrases} />
            <ProductionNotesPanel notes={script.production_notes} />
          </div>
        )}
      </div>
    </div>
  )
}
